import React, { useCallback, useContext, useMemo } from "react";

import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fallback, Image, Root } from "@radix-ui/react-avatar";
import cx from "classnames";
import { isNil } from "lodash";
import invariant from "tiny-invariant";

import ThemeContext from "~/common/ThemeContext";
import { getAnonymousUserIcon, isiOSApp, str2AttendeeColors, uiAvatar } from "~/lib/utils";

import { ReactComponent as ConnectedOutlinedDarkIcon } from "./connected-outlined-dark.svg";
import { ReactComponent as ConnectedOutlinedIcon } from "./connected-outlined.svg";
import { ReactComponent as GuestIcon } from "./guest.svg";
import { ReactComponent as TopUserIcon } from "./top-user.svg";

import styles from "./Avatar.module.css";

declare module "csstype" {
	interface Properties {
		"--avatar-outline-color"?: string;
		"--avatar-fallback-color"?: string;
	}
}

type Outline = "none" | "dashed" | "solid";

function useColorsForText(textForColor: string) {
	return useMemo(() => {
		const { ring: outlineColor, tooltip: fallbackColor } = str2AttendeeColors(textForColor);
		return { outlineColor, fallbackColor };
	}, [textForColor]);
}

function IconContent({ icon, outline = "none" }: { icon: IconDefinition; outline?: Outline }) {
	return (
		<span className={cx(styles.fontAwesomeImage, { [styles.outline]: outline !== "none" })}>
			<FontAwesomeIcon icon={icon} />
		</span>
	);
}

export interface AvatarUserAttrs {
	/** Something to show as the name. Could be something else if you don't have a name, like an email. */
	fullName: string;
	image?: string;
	isExternal?: boolean;
	isGuest?: boolean;
	isTopUser?: boolean;
	isAnonymous?: boolean;
}

export interface AvatarProps {
	className?: string;
	size?: "small" | "medium" | "large";
	outline?: Outline;
	hideIcon?: boolean;
	grayscale?: boolean;
}

export interface UserAvatarProps extends AvatarProps {
	user: AvatarUserAttrs;
}

type IconType = "external" | "guest" | "topUser";
const Icon = ({ type }: { type: IconType }) => {
	const { isDarkMode } = useContext(ThemeContext);

	switch (type) {
		case "external": {
			return isDarkMode ? (
				<ConnectedOutlinedDarkIcon className={styles.icon} />
			) : (
				<ConnectedOutlinedIcon className={styles.icon} />
			);
		}
		case "guest": {
			return <GuestIcon className={styles.icon} />;
		}
		case "topUser": {
			return <TopUserIcon className={styles.icon} />;
		}

		default:
			return null;
	}
};

const UserAvatar = React.forwardRef<HTMLSpanElement, UserAvatarProps>(function UserAvatar(
	{ user, size = "medium", outline = "none", hideIcon, className, grayscale = false },
	ref,
) {
	// There are cases where user is `any`, and we lose out on the enforcement of `fullName`'s type.
	invariant(
		!isNil(user.fullName),
		"[Avatar.tsx] fullName was not defined. Could be something else if you don't have a name, like an email.",
	);

	const workingName = user.fullName;
	const fallbackImage = useMemo(() => {
		// This is not the same as workingName[0] - if workingName starts with a surrogate pair,
		// e.g. an emoji, this will be the whole character, not just the first byte.
		// For details see https://stackoverflow.com/q/21397316/1612471
		const [firstChar] = workingName;
		return uiAvatar(firstChar?.toUpperCase() ?? "?");
	}, [workingName]);

	const iconType = useMemo(() => {
		if (user.isAnonymous) return null;
		if (user.isExternal) return "external";
		if (user.isGuest) return "guest";
		if (user.isTopUser) return "topUser";
		return null;
	}, [user]);

	const helpText = useMemo(() => {
		if (user.isAnonymous) return `Anonymous ${workingName}`;

		switch (iconType) {
			case "external":
				return `${workingName} (External)`;
			case "guest":
				return `${workingName} (Guest)`;
			case "topUser":
				return `${workingName} (Top User)`;
			default:
				return undefined;
		}
	}, [user.isAnonymous, workingName, iconType]);

	const { outlineColor, fallbackColor } = useColorsForText(workingName);

	const preventDefaultIfIniOSApp = useCallback((e: Event | React.SyntheticEvent) => {
		if (isiOSApp) {
			e.preventDefault();
		}
	}, []);

	return (
		<Root
			ref={ref}
			title={helpText}
			className={cx(styles.root, styles[size], className, { [styles[`outline-${outline}`]]: outline !== "none" })}
			style={{
				"--avatar-outline-color": `#${outlineColor}`,
				"--avatar-fallback-color": `#${fallbackColor}`,
			}}
		>
			{user.isAnonymous ? (
				<IconContent icon={getAnonymousUserIcon(workingName)} outline={outline} />
			) : !user.image || user.image === "" ? (
				<img
					src={fallbackImage}
					className={cx(styles.fallback, {
						[styles.outline]: outline !== "none",
						[styles.grayscaleFilter]: grayscale,
					})}
					onDragStart={preventDefaultIfIniOSApp}
				/>
			) : (
				<>
					<Image
						className={cx(styles.image, {
							[styles.outline]: outline !== "none",
							[styles.grayscaleFilter]: grayscale,
						})}
						src={user.image}
						alt={helpText}
						onDragStart={preventDefaultIfIniOSApp}
					/>
					<Fallback
						asChild
						className={cx(styles.fallback, {
							[styles.outline]: outline !== "none",
							[styles.grayscaleFilter]: grayscale,
						})}
					>
						<img src={fallbackImage} />
					</Fallback>
					{!hideIcon && iconType && <Icon type={iconType} />}
				</>
			)}
		</Root>
	);
});

export default React.memo(UserAvatar);

export interface AccountAttrs {
	name: string;
	image?: string;
}

export interface AccountAvatarProps extends Omit<AvatarProps, "outline" | "hideIcon"> {
	account: AccountAttrs;
}

export const AccountAvatar = React.forwardRef<HTMLSpanElement, AccountAvatarProps>(function AccountAvatar(
	{ account, className, ...rest },
	ref,
) {
	const user = React.useMemo<AvatarUserAttrs>(
		() => ({
			fullName: account.name,
			image: account.image,
		}),
		[account],
	);
	return <UserAvatar {...rest} user={user} ref={ref} className={cx(className, styles.accountAvatar)} />;
});

export interface TeamAttrs {
	title: string;
	id: string;
}

export interface TeamAvatarProps extends Omit<AvatarProps, "hideIcon"> {
	team: TeamAttrs;
}

export const TeamAvatar = React.forwardRef<HTMLSpanElement, TeamAvatarProps>(function TeamAvatar(
	{ team, className, size = "medium", outline = "none" },
	ref,
) {
	const { outlineColor, fallbackColor } = useColorsForText(team.title);

	return (
		<Root
			ref={ref}
			title={team.title}
			className={cx(styles.root, styles[size], className, { [styles[`outline-${outline}`]]: outline !== "none" })}
			style={{
				"--avatar-outline-color": `#${outlineColor}`,
				"--avatar-fallback-color": `#${fallbackColor}`,
			}}
		>
			<IconContent icon={faUsers} outline={outline} />
		</Root>
	);
});
